// Push notification bootstrap for NoteLosh.
//
// Picks the right transport for the host platform:
//   • Capacitor Android → @capacitor/push-notifications (native FCM)
//   • Web / Capacitor iOS web view fallback → firebase/messaging (web push)
//
// Both end up POSTing their FCM registration token to /api/push/register so
// the backend can fan reminder pushes out to every device the user owns.
//
// Called once from Workspace after the user is authenticated. All side-effects
// are best-effort: a missing permission, an unsupported browser, or a service
// worker that fails to register just quietly skips — the rest of the app keeps
// working.

import { api } from "./api";

const isNative = () =>
  typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

const platform = () => {
  try {
    return window.Capacitor?.getPlatform?.() || "web";
  } catch {
    return "web";
  }
};

const supportsWebPush = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "Notification" in window &&
  !!window.PushManager;

let _lastToken = null;
let _initPromise = null;

async function registerTokenWithBackend(token, plat) {
  _lastToken = token;
  try {
    await api.post("/push/register", { token, platform: plat });
  } catch (e) {
    // Non-fatal: user just won't get pushes until next retry.
    console.warn("[push] backend register failed:", e?.message || e);
  }
}

async function initWebPush() {
  if (!supportsWebPush()) {
    console.info("[push] web push not supported in this browser — skipping");
    return;
  }
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    console.warn("[push] missing REACT_APP_FIREBASE_VAPID_KEY — skipping");
    return;
  }
  let perm = Notification.permission;
  if (perm === "default") {
    try {
      perm = await Notification.requestPermission();
    } catch {
      perm = "denied";
    }
  }
  if (perm !== "granted") {
    console.info("[push] permission:", perm);
    return;
  }
  const swReg = await navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .catch((e) => {
      console.warn("[push] SW register failed:", e?.message || e);
      return null;
    });
  if (!swReg) return;

  const { initializeApp, getApps } = await import("firebase/app");
  const { getMessaging, getToken, onMessage } = await import("firebase/messaging");

  const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
  };
  const app = getApps()[0] || initializeApp(config);
  const messaging = getMessaging(app);

  let token = null;
  try {
    token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swReg,
    });
  } catch (e) {
    console.warn("[push] getToken failed:", e?.message || e);
    return;
  }
  if (!token) return;

  await registerTokenWithBackend(token, "web");

  // Foreground pushes: the SW doesn't fire when the tab is focused. Lightly
  // notify in-app so the user still sees the reminder.
  onMessage(messaging, (payload) => {
    const title = payload?.notification?.title || "NoteLosh reminder";
    const body = payload?.notification?.body || "";
    try {
      // sonner is already globally mounted; use its global signal if present.
      if (window.sonnerToast) {
        window.sonnerToast(title + (body ? " — " + body : ""));
      } else if ("Notification" in window && Notification.permission === "granted") {
        new Notification(title, { body });
      }
    } catch {
      /* ignore */
    }
  });
}

async function initNativePush() {
  try {
    const mod = await import("@capacitor/push-notifications");
    const PushNotifications = mod.PushNotifications;
    if (!PushNotifications) return;

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") {
      console.info("[push] native permission:", perm.receive);
      return;
    }

    PushNotifications.addListener("registration", (token) => {
      if (token?.value) {
        registerTokenWithBackend(token.value, platform());
      }
    });
    PushNotifications.addListener("registrationError", (err) => {
      console.warn("[push] native registration error:", err);
    });
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const link = action?.notification?.data?.link;
      if (link && link.startsWith("/") && link !== "/app") window.location.href = link;
    });

    // Android 8+ silently drops pushes targeting an unknown channel. The
    // backend sends every reminder with channel_id="notelosh_reminders", so
    // make sure that channel exists on the device before registering. iOS
    // ignores createChannel, so this is safe to call unconditionally.
    if (platform() === "android" && PushNotifications.createChannel) {
      try {
        await PushNotifications.createChannel({
          id: "notelosh_reminders",
          name: "Reminders",
          description: "Reminders you set inside NoteLosh",
          importance: 4, // HIGH — pops a heads-up card
          visibility: 1,
          lights: true,
          vibration: true,
        });
      } catch (e) {
        console.warn("[push] createChannel failed:", e?.message || e);
      }
    }

    await PushNotifications.register();
  } catch (e) {
    console.warn("[push] native init failed:", e?.message || e);
  }
}

export function initPushNotifications() {
  if (_initPromise) return _initPromise;
  _initPromise = (isNative() ? initNativePush() : initWebPush()).catch(
    (e) => {
      console.warn("[push] init failed:", e?.message || e);
    }
  );
  return _initPromise;
}

export async function unregisterPushNotifications() {
  // Called from logout — detach this device from the backend fan-out so a
  // shared browser doesn't keep receiving reminders for a user who left.
  if (!_lastToken) return;
  try {
    await api.post("/push/unregister", { token: _lastToken });
  } catch (_e) { /* ignore */ }
  _lastToken = null;
  _initPromise = null;
}
